import {
  FileText, Download, Upload, Image, Copy, Cloud, Share2,
  HelpCircle, BookOpen, Sparkles, Lightbulb, Heart, Bug,
  Undo2, Redo2, Play, ListTree, Trash2, Rocket, Wand2, FolderUp,
  Save, FolderOpen, FileJson, Bot, Cog, Globe,
} from 'lucide-react';
import { usePlan } from '@/hooks/usePlan';
import { useAiEnabled } from '@/hooks/useAiEnabled';
import { useSystemSwitches } from '@/hooks/useSystemSwitches';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useTranslation } from '@/i18n/LanguageContext';
import { IS_WEB } from '@/lib/buildMode';
import { BRAND_NAVY } from '@/constants/brandColors';
import { CreditBalanceBadge } from '@/components/workflow/CreditBalanceBadge';
import { useNavigate } from 'react-router-dom';

export interface TopBarDesktopActionsProps {
  isAuthenticated: boolean;
  canUndo?: boolean;
  canRedo?: boolean;
  onSave: () => void;
  /** Spara till molnet — uppdaterar existerande doc om currentDocumentId är satt. */
  onSaveToCloud?: () => void;
  /** True när workflow:et redan ligger i molnet. Styr "Spara"/"Uppdatera"-text. */
  hasCloudDocument?: boolean;
  /** Öppna NewWorkflowDialog (type-chooser för nytt flöde). */
  onNewFlow?: () => void;
  onLoad: () => void;
  /** Öppna Repository-uppladdningsdialog (.zip → workflow-scan). */
  onRepoUpload?: () => void;
  onExportPNG: () => void;
  onImportJson: () => void;
  onCopyJson: () => void;
  onHelp: () => void;
  onAiJson: () => void;
  onAiAssistant: () => void;
  onSimulate: () => void;
  onPreview: () => void;
  onCloudWorkspace?: () => void;
  onShareWorkspace?: () => void;
  onProcessMotor?: () => void;
  /** Re-trigga "What are you building?"-wizard:n. Användbart för att seeda en ny sheet. */
  onReopenWizard?: () => void;
  onFeatureRequest?: () => void;
  onBugReport?: () => void;
  onDonate?: () => void;
  onClear: () => void;
  onUndo?: () => void;
  onRedo?: () => void;
}

const NAVY_BG = { backgroundColor: BRAND_NAVY } as const;

export function TopBarDesktopActions(props: TopBarDesktopActionsProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { limits } = usePlan();
  const ai = useAiEnabled();
  const { switches } = useSystemSwitches();
  const processEngineDisabled = !switches.processEngine;
  const {
    isAuthenticated, canUndo, canRedo,
    onSave, onSaveToCloud, hasCloudDocument, onNewFlow,
    onLoad, onRepoUpload, onExportPNG, onImportJson, onCopyJson,
    onHelp, onAiJson, onAiAssistant, onSimulate, onPreview,
    onCloudWorkspace, onShareWorkspace, onProcessMotor, onReopenWizard, onFeatureRequest, onBugReport, onDonate,
    onClear, onUndo, onRedo,
  } = props;
  // Process-motor-knapp visas bara för Enterprise-tier när workflow-level-
  // callback är satt (Index-sidan dirigerar det). Non-enterprise ser inte
  // knappen alls för att undvika "nej, du får inte"-förvirring.
  const showProcessMotor = limits.canRunProcesses && typeof onProcessMotor === 'function';
  const aiOffTooltip = ai.systemDisabledByAdmin
    ? 'AI är avstängd av admin'
    : ai.blockedByTier
      ? 'AI kräver Advanced eller högre'
      : null;

  return (
    <>
      <DropdownMenu>
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="text-white hover:opacity-90 rounded-md focus-visible:ring-white focus-visible:ring-offset-0"
                style={NAVY_BG}
                data-testid="topbar-file-menu"
                data-guide="topbar-file-menu"
                aria-label={`${t('saveFile')} / ${t('loadFile')}`}
              >
                <span className="flex items-center">
                  <Save className="w-4 h-4" aria-hidden="true" />
                  <FolderOpen className="w-3.5 h-3.5 -ml-0.5" aria-hidden="true" />
                </span>
              </Button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent>{t('saveFile')} / {t('loadFile')}</TooltipContent>
        </Tooltip>
        <DropdownMenuContent align="start" className="w-52">
          {onReopenWizard && (
            <>
              <DropdownMenuItem onClick={onReopenWizard} data-testid="topbar-menu-wizard">
                <Wand2 className="w-4 h-4 mr-2" />
                Workflow Wizard…
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          )}
          {onNewFlow && (
            <>
              <DropdownMenuItem onClick={onNewFlow} data-testid="topbar-new-flow">
                <Sparkles className="w-4 h-4 mr-2" />
                Nytt flöde…
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          )}
          <DropdownMenuItem onClick={onSave} data-testid="topbar-save">
            <Download className="w-4 h-4 mr-2" />
            <span className="flex-1">{t('saveFile')}</span>
            <span className="ml-2 text-[10px] text-muted-foreground">Ctrl+S</span>
          </DropdownMenuItem>
          {isAuthenticated && onSaveToCloud && (
            <DropdownMenuItem onClick={onSaveToCloud} data-testid="topbar-save-cloud">
              <Cloud className="w-4 h-4 mr-2" />
              {hasCloudDocument ? 'Uppdatera molnkopia' : 'Spara till molnet'}
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={onLoad} data-testid="topbar-load">
            <Upload className="w-4 h-4 mr-2" /> {t('loadFile')}
          </DropdownMenuItem>
          {onRepoUpload && (
            <DropdownMenuItem onClick={onRepoUpload} data-testid="topbar-repo-upload">
              <FolderUp className="w-4 h-4 mr-2" /> Ladda upp repository (.zip)
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={onExportPNG} data-testid="topbar-export">
            <Image className="w-4 h-4 mr-2" /> {t('exportPng')}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={onImportJson} data-testid="topbar-import">
            <Upload className="w-4 h-4 mr-2" /> {t('import_')}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={onCopyJson} data-testid="topbar-copy-json">
            <Copy className="w-4 h-4 mr-2" /> {t('copyJson')}
          </DropdownMenuItem>
          {isAuthenticated && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={onCloudWorkspace} data-testid="topbar-cloud">
                <Cloud className="w-4 h-4 mr-2" /> Molnarbetsytor
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onShareWorkspace} data-testid="topbar-share">
                <Share2 className="w-4 h-4 mr-2" /> Dela arbetsyta
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            onClick={onHelp}
            className="text-white hover:opacity-90 rounded-md focus-visible:ring-white focus-visible:ring-offset-0"
            style={NAVY_BG}
            data-testid="topbar-help"
            aria-label={t('help')}
          >
            <HelpCircle className="w-4 h-4" aria-hidden="true" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>{t('help')}</TooltipContent>
      </Tooltip>

      {onReopenWizard && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onReopenWizard}
          className="text-white hover:opacity-90 rounded-md focus-visible:ring-white focus-visible:ring-offset-0"
          style={NAVY_BG}
          data-testid="topbar-reopen-wizard"
          data-guide="topbar-reopen-wizard"
          title="Re-trigga 'What are you building?'-wizard för att seeda en ny sheet"
        >
          <Wand2 className="w-4 h-4 mr-2" aria-hidden="true" />
          {t('welcome')}
        </Button>
      )}

      {isAuthenticated && (
        <>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={onAiJson}
                disabled={!ai.enabled}
                className="text-white hover:opacity-90 rounded-md disabled:opacity-40"
                style={NAVY_BG}
                data-testid="topbar-ai-json"
                aria-label="AI & JSON Reference"
              >
                <FileJson className="w-4 h-4" aria-hidden="true" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{aiOffTooltip ?? 'AI & JSON Reference'}</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={onAiAssistant}
                disabled={!ai.enabled}
                className="text-white hover:opacity-90 rounded-md disabled:opacity-40"
                style={NAVY_BG}
                data-guide="topbar-ai"
                data-testid="topbar-ai-assistant"
                aria-label="AI Assistant"
              >
                <Bot className="w-4 h-4" aria-hidden="true" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{aiOffTooltip ?? 'AI Assistant'}</TooltipContent>
          </Tooltip>
          {showProcessMotor && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onProcessMotor}
                  disabled={processEngineDisabled}
                  className="text-white hover:opacity-90 rounded-md focus-visible:ring-white focus-visible:ring-offset-0 disabled:opacity-40"
                  style={{ backgroundColor: '#7c3aed' }}
                  data-testid="topbar-process-motor"
                  data-guide="topbar-process-motor"
                  aria-label="Kör workflow som process"
                >
                  <Rocket className="w-4 h-4" aria-hidden="true" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                {processEngineDisabled ? 'Process Engine är avstängd av admin' : 'Kör workflow som process (Enterprise)'}
              </TooltipContent>
            </Tooltip>
          )}
        </>
      )}

      {IS_WEB && (
        <>
          <CreditBalanceBadge onClick={() => navigate('/billing')} />
          <Button variant="ghost" size="sm" onClick={onFeatureRequest} className="text-white hover:opacity-90 rounded-md focus-visible:ring-white focus-visible:ring-offset-0" style={NAVY_BG} data-testid="topbar-feature-request">
            <Lightbulb className="w-4 h-4 mr-2" aria-hidden="true" />
            Feature Request
          </Button>
          <Button variant="ghost" size="sm" onClick={onBugReport} className="text-white hover:opacity-90 rounded-md focus-visible:ring-white focus-visible:ring-offset-0" style={{ backgroundColor: '#b91c1c' }} data-testid="topbar-bug-report" title="Report a bug">
            <Bug className="w-4 h-4 mr-2" aria-hidden="true" />
            Bug
          </Button>
          <Button variant="ghost" size="sm" onClick={onDonate} className="text-white hover:opacity-90 rounded-md focus-visible:ring-white focus-visible:ring-offset-0" style={{ backgroundColor: '#e91e63' }} data-testid="topbar-donate">
            <Heart className="w-4 h-4 mr-2" aria-hidden="true" />
            {t('donate')}
          </Button>
        </>
      )}

      <div className="w-px h-6 mx-1" style={{ backgroundColor: BRAND_NAVY, opacity: 0.2 }} aria-hidden="true" />

      <Button variant="ghost" size="icon" onClick={onUndo} disabled={!canUndo}
        className="hover:opacity-90 rounded-md disabled:opacity-40 focus-visible:ring-white focus-visible:ring-offset-0"
        style={{ backgroundColor: BRAND_NAVY, color: 'white' }}
        title="Ångra (Ctrl+Z)" aria-label="Undo"
        data-guide="topbar-undo" data-testid="topbar-undo">
        <Undo2 className="w-4 h-4" aria-hidden="true" />
      </Button>
      <Button variant="ghost" size="icon" onClick={onRedo} disabled={!canRedo}
        className="hover:opacity-90 rounded-md disabled:opacity-40 focus-visible:ring-white focus-visible:ring-offset-0"
        style={{ backgroundColor: BRAND_NAVY, color: 'white' }}
        title="Gör om (Ctrl+Shift+Z)" aria-label="Redo"
        data-guide="topbar-redo" data-testid="topbar-redo">
        <Redo2 className="w-4 h-4" aria-hidden="true" />
      </Button>

      {isAuthenticated && (
        <>
          <div className="w-px h-6 mx-1" style={{ backgroundColor: BRAND_NAVY, opacity: 0.2 }} aria-hidden="true" />
          <Button
            variant="ghost"
            size="sm"
            disabled
            aria-disabled="true"
            title="Tillfälligt avstängd / Temporarily disabled"
            className="text-white rounded-md opacity-40 cursor-not-allowed"
            style={NAVY_BG}
            data-testid="topbar-simulate"
          >
            <Play className="w-4 h-4 mr-2" aria-hidden="true" />
            {t('simulate')}
          </Button>
          <Button variant="ghost" size="sm" onClick={onPreview} className="text-white hover:opacity-90 rounded-md focus-visible:ring-white focus-visible:ring-offset-0" style={NAVY_BG} data-testid="topbar-preview">
            <ListTree className="w-4 h-4 mr-2" aria-hidden="true" />
            Overview
          </Button>
        </>
      )}

      <div className="w-px h-6 mx-1" style={{ backgroundColor: BRAND_NAVY, opacity: 0.2 }} aria-hidden="true" />

      <Button variant="ghost" size="sm" onClick={onClear} className="text-white hover:opacity-90 rounded-md focus-visible:ring-white focus-visible:ring-offset-0" style={{ backgroundColor: '#dc2626' }} data-testid="topbar-clear">
        <Trash2 className="w-4 h-4 mr-2" aria-hidden="true" />
        {t('clear')}
      </Button>
    </>
  );
}
